import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  TrendUp, 
  ArrowLeft,
  Funnel,
  ArrowsClockwise
} from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { StockNewsGrid } from "@/components/StockNews";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HeaderStickyBanner } from "@/components/AdPlacements";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function StockNewsListPage() {
  const { user, token } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?country=${filter}` : "";
      const response = await axios.get(`${API}/stock-news${params}`);
      setNews(response.data);
    } catch (error) {
      console.error("Error fetching stock news:", error);
      toast.error("Failed to load stock news");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const handleRefresh = async () => {
    if (!user || user.role !== "admin") {
      toast.error("Admin access required");
      return;
    }

    setRefreshing(true);
    try {
      await axios.post(`${API}/stock-news/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Stock news refresh started");
      // Refresh the list after a delay
      setTimeout(fetchNews, 5000);
    } catch (error) {
      toast.error("Failed to refresh stock news");
    } finally {
      setRefreshing(false);
    }
  };

  const filters = [
    { value: "all", label: "All Markets" },
    { value: "uk", label: "🇬🇧 UK (LSE)" },
    { value: "ireland", label: "🇮🇪 Ireland" },
    { value: "usa", label: "🇺🇸 USA (NYSE)" },
  ];

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEOHead 
        title="Agri Stock News"
        url="/stock-news"
        description="Latest news and press releases from agriculture companies. Monitor Wynnstay, Origin Enterprises, Deere, Nutrien, Kerry Group, and more."
        keywords="agriculture stock news, farming company news, agri stocks, RNS announcements"
      />
      <HeaderStickyBanner />
      <Header />
      
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        {/* Back Link */}
        <Link
          to="/agri-stocks"
          className="inline-flex items-center gap-2 text-[#6B6661] hover:text-[#0F766E] mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm">Back to Stock Watchlist</span>
        </Link>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <TrendUp size={28} className="text-[#0F766E]" weight="duotone" />
              <h1 className="font-serif text-3xl text-[#33302E]">Agri Stock News</h1>
            </div>
            <p className="text-[#6B6661]">
              Latest news and announcements from agriculture companies
            </p>
          </div>
          
          {user?.role === "admin" && (
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-stock-news-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white hover:bg-[#064E3B] transition-colors disabled:opacity-50"
            >
              <ArrowsClockwise size={18} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing..." : "Refresh News"}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          <Funnel size={18} className="text-[#6B6661] flex-shrink-0" />
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                filter === f.value
                  ? "bg-[#0F766E] text-white"
                  : "bg-white border border-[#E0D4C8] text-[#374151] hover:border-[#0F766E]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* News Grid */}
        <StockNewsGrid news={news} loading={loading} />

        {/* Info Box */}
        <div className="mt-12 bg-white border border-[#E0D4C8] p-6">
          <h2 className="font-serif text-xl text-[#33302E] mb-4">About Agri Stock News</h2>
          <p className="text-sm text-[#6B6661] mb-4">
            This section monitors press releases and RNS (Regulatory News Service) announcements from 
            agriculture companies listed on the London Stock Exchange, Euronext Dublin, and NYSE.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-[#F9FAFB] border border-[#E0D4C8]">
              <div className="font-semibold text-[#33302E] mb-1">UK (LSE)</div>
              <div className="text-[#6B6661]">Wynnstay, Origin, NWF, Carr's, Genus, Camellia, Cranswick</div>
            </div>
            <div className="p-3 bg-[#F9FAFB] border border-[#E0D4C8]">
              <div className="font-semibold text-[#33302E] mb-1">Ireland</div>
              <div className="text-[#6B6661]">Origin Enterprises, Kerry Group, Glanbia, FBD Holdings</div>
            </div>
            <div className="p-3 bg-[#F9FAFB] border border-[#E0D4C8]">
              <div className="font-semibold text-[#33302E] mb-1">USA (NYSE)</div>
              <div className="text-[#6B6661]">Deere, Nutrien, CF Industries, ADM, Bunge, Corteva, AGCO</div>
            </div>
          </div>
        </div>

        {/* Bottom Leaderboard Ad */}
        <div className="mt-8">
        </div>
      </main>

      <Footer />
    </div>
  );
}
